import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { BarChart3 } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { OverviewFilters, OverviewData, WebsiteOverviewData, SOLUTIONS, PERIODS } from '../../types/overview';
import { getOverviewRevenue, getOverviewWebsiteRevenue } from '../../api/overviewApi';

// UI Components
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Alert, AlertDescription, AlertTitle } from '../ui/alert';

// Overview Components
import { OverviewFiltersComponent } from './OverviewFilters';
import { RevenueChart } from './RevenueChart';

export const OverviewDashboard: React.FC = () => {
  const { t } = useTranslation();
  const { currentUser } = useAuth();
  
  // États
  const [overviewData, setOverviewData] = useState<OverviewData | null>(null);
  const [websiteData, setWebsiteData] = useState<WebsiteOverviewData | null>(null);
  
  // Calculer les dates initiales pour "yesterday"
  const getInitialDates = () => {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    return {
      customStartDate: yesterday.toISOString().split('T')[0],
      customEndDate: yesterday.toISOString().split('T')[0]
    };
  };
  
  const [filters, setFilters] = useState<OverviewFilters>({
    solution: SOLUTIONS.SSP,
    period: PERIODS.YESTERDAY,
    showVariation: false,
    ...getInitialDates()
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Récupérer les données du dashboard
  const fetchOverviewData = async (currentFilters?: OverviewFilters) => {
    try {
      setIsLoading(true);
      setError(null);
      const filtersToUse = currentFilters || filters;
      const response = await getOverviewRevenue({
        solution: filtersToUse.solution,
        period: filtersToUse.period,
        showVariation: filtersToUse.showVariation,
        customStartDate: filtersToUse.customStartDate,
        customEndDate: filtersToUse.customEndDate
      });
      setOverviewData(response.data);
    } catch (error: any) {
      console.error('Error fetching overview data:', error);
      setError(error.message || t('overview.loading'));
    } finally {
      setIsLoading(false);
    }
  };

  // Charger les données initiales
  useEffect(() => {
    if (currentUser?.role === 'ADMIN') {
      fetchOverviewData();
    }
  }, [currentUser]);

  // Gestionnaire de changement de filtres
  const handleFiltersChange = (newFilters: OverviewFilters) => {
    setFilters(newFilters);
    // Reset website data when changing filters
    setWebsiteData(null);
    fetchOverviewData(newFilters);
  };

  // Fonction pour récupérer les données des websites d'un network
  const fetchWebsiteData = async (networkId: number) => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await getOverviewWebsiteRevenue({
        solution: filters.solution,
        period: filters.period,
        showVariation: filters.showVariation,
        networkId,
        customStartDate: filters.customStartDate,
        customEndDate: filters.customEndDate
      });
      setWebsiteData(response.data);
    } catch (error: any) {
      console.error('Error fetching website data:', error);
      setError(error.message || 'Failed to fetch website data');
    } finally {
      setIsLoading(false);
    }
  };

  // Gestionnaire de clic sur un network (drill down)
  const handleNetworkClick = (networkId: number, _networkName: string) => {
    fetchWebsiteData(networkId);
  };

  // Gestionnaire de retour à la vue networks
  const handleBackToNetworks = () => {
    setWebsiteData(null);
  };

  // Vérifier l'accès admin
  if (!currentUser) {
    return (
      <div className="p-6">
        <Alert variant="destructive">
          <AlertTitle>{t('common.error')}</AlertTitle>
          <AlertDescription>{t('auth.notAuthenticated')}</AlertDescription>
        </Alert>
      </div>
    );
  }

  if (currentUser.role !== 'ADMIN') {
    return (
      <div className="p-6">
        <Alert variant="destructive">
          <AlertTitle>{t('common.error')}</AlertTitle>
          <AlertDescription>Access denied. Admin role required.</AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-7rem)] overflow-auto">
      <div className="p-6 space-y-6">
        {/* En-tête */}
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-2">
              <BarChart3 className="h-8 w-8 text-primary" />
              {t('overview.title')}
            </h1>
            <p className="text-muted-foreground mt-1">
              {t('overview.subtitle')}
            </p>
          </div>
        </div>

        {/* Filtres intégrés */}
        <Card className="animate-in fade-in duration-500">
          <CardHeader>
            <CardTitle>Filters</CardTitle>
          </CardHeader>
          <CardContent>
            <OverviewFiltersComponent
              filters={filters}
              onChange={handleFiltersChange}
              isLoading={isLoading}
            />
          </CardContent>
        </Card>

        {/* Messages d'erreur */}
        {error && (
          <Alert variant="destructive">
            <AlertTitle>{t('common.error')}</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

                {/* Résumé des métriques */}
                {overviewData && (
                  <Card className="animate-in fade-in duration-500 delay-300">
                    <CardHeader>
                      <CardTitle>Revenue Summary</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="text-center">
                          <p className="text-sm text-muted-foreground">Total Revenue</p>
                          <p className="text-2xl font-bold">
                            {new Intl.NumberFormat('en-US', {
                              style: 'currency',
                              currency: overviewData.currency
                            }).format(overviewData.totalRevenue)}
                          </p>
                        </div>
                        <div className="text-center">
                          <p className="text-sm text-muted-foreground">Active Networks</p>
                          <p className="text-2xl font-bold">{overviewData.networks.length}</p>
                        </div>
                        {overviewData.totalVariation !== undefined && (
                          <div className="text-center">
                            <p className="text-sm text-muted-foreground">{t('overview.variation')}</p>
                            <p className={`text-2xl font-bold ${
                              overviewData.totalVariation >= 0 ? 'text-green-600' : 'text-red-600'
                            }`}>
                              {overviewData.totalVariation >= 0 ? '+' : ''}
                              {overviewData.totalVariation.toFixed(1)}%
                            </p>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                )}

        {/* Graphique des revenus */}
        <RevenueChart
          data={overviewData}
          websiteData={websiteData}
          isLoading={isLoading}
          showVariation={filters.showVariation}
          onNetworkClick={handleNetworkClick}
          onBackToNetworks={handleBackToNetworks}
        />

        {/* État de chargement */}
        {isLoading && !overviewData && (
          <Card>
            <CardContent className="p-12">
              <div className="text-center space-y-4">
                <div className="mx-auto w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                  <BarChart3 className="h-6 w-6 text-primary animate-pulse" />
                </div>
                <p className="text-muted-foreground">{t('overview.loading')}</p>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};